import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
 
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
 
const LoginForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
 
  // Initialize with saved credentials
  const getSavedCredentials = () => {
    const savedEmail = localStorage.getItem("rememberedEmail") || "";
    const savedPassword = localStorage.getItem("rememberedPassword") || "";
    console.log("Loading saved credentials:", { savedEmail, savedPassword: savedPassword ? "***" : "" });
    return {
      email: savedEmail,
      password: savedPassword,
      remember: !!savedEmail
    };
  };

  const savedCredentials = getSavedCredentials();

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(savedCredentials.remember);
  const [initialEmail, setInitialEmail] = useState(savedCredentials.email);
  const [initialPassword, setInitialPassword] = useState(savedCredentials.password);

  const initialValues = {
    email: initialEmail,
    password: initialPassword,
    remember: rememberMe,
  };
 
  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email format")
      .matches(
        /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
        "Email must be filled with proper domain"
      )
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });
 
  const onSubmit = async (values, { setSubmitting, setErrors }) => {
    try {
      await login(values.email, values.password);

      // If login successful, save credentials if remember is checked
      if (values.remember) {
        localStorage.setItem("rememberedEmail", values.email);
        localStorage.setItem("rememberedPassword", values.password);
        console.log("Credentials saved to localStorage");
      } else {
        localStorage.removeItem("rememberedEmail");
        localStorage.removeItem("rememberedPassword");
        console.log("Credentials removed from localStorage");
      }

      navigate("/");
    } catch (error) {
      console.error("Login error:", error);
      // Clear remembered credentials on login failure
      localStorage.removeItem("rememberedEmail");
      localStorage.removeItem("rememberedPassword");
      setRememberMe(false);

      // Set form-level error
      if (error.message) {
        setErrors({ loginError: error.message });
      } else {
        setErrors({ loginError: "Login failed. Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  };
 
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };
 
  return (
    <Formik
      key={`${initialEmail}-${initialPassword}-${rememberMe}`} // Force re-render when initial values change
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, values, setFieldValue, errors, handleChange, handleBlur }) => (
        <Form>
          <div className="mb-3">
            <h6 className="auth">
              Fill out the information below in order to access your account.
            </h6>
            <label className="form-label required" htmlFor="email">
              Email
            </label>
            <input
              type="email"
              name="email"
              className="form-control"
              placeholder="Enter your email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <ErrorMessage
              name="email"
              component="div"
              className="invalid-feedback d-block"
            />
          </div>

          <div className="mb-3 position-relative">
            <label className="form-label required" htmlFor="password">
              Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              className="form-control"
              placeholder="Enter your password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <span onClick={handleTogglePassword} className="inputicon">
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </span>
            <ErrorMessage
              name="password"
              component="div"
              className="invalid-feedback d-block"
            />
          </div>
 
          <div className="d-flex mb-3 justify-content-between align-items-center">
            <div className="form-check">
              <input
                type="checkbox"
                name="remember"
                className="form-check-input"
                id="remember"
                checked={values.remember}
                onChange={() => setFieldValue("remember", !values.remember)}
              />
              <label className="form-check-label" htmlFor="remember">
                Remember Password
              </label>
            </div>
            <div className="text-end mb-3">
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => navigate("/forgot-password")}
              >
                Forgot Password?
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary w-100 mt-0"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Logging in..." : "Login"}
          </button>
 
          {errors.loginError && (
            <div className="invalid-feedback d-block text-center mt-3 fs-6">
              {errors.loginError}
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
export default LoginForm;